import type { Feature, Polygon, MultiPolygon, Point, Position } from "geojson";
import { polygon } from "@turf/helpers";
import area from "@turf/area";
import centroid from "@turf/centroid";
import polygonToLine from "@turf/polygon-to-line";
import lineIntersect from "@turf/line-intersect";
import booleanContains from "@turf/boolean-contains";
import booleanWithin from "@turf/boolean-within";
import booleanPointInPolygon from "@turf/boolean-point-in-polygon";
import { distanceToEdges, metresPerDegree } from "./planar";
import type { AnyPolygonFeature, PointFeature, ValidationResult } from "../types";

/** Split any Polygon/MultiPolygon into an array of single Polygon features */
function toPolygonArray(feat: AnyPolygonFeature): Feature<Polygon>[] {
	if (feat.geometry.type === "Polygon") {
		return [feat as Feature<Polygon>];
	}
	return (feat as Feature<MultiPolygon>).geometry.coordinates.map((coords) => polygon(coords));
}

/** Filter intersection points that are actual ring vertices (not real intersections) */
function getRealIntersections(
	intersections: GeoJSON.FeatureCollection<Point>,
	ring: GeoJSON.Position[],
): Feature<Point>[] {
	return intersections.features.filter((f) => {
		const [x, y] = f.geometry.coordinates;
		return !ring.some(([vx, vy]) => vx === x && vy === y);
	});
}

/**
 * Validates a Polygon or MultiPolygon for geometric correctness:
 * - No self-intersections in outer rings or holes
 * - Holes don't cross outer rings
 * - Holes don't intersect each other
 * - MultiPolygon parts don't overlap
 */
export function isPolygonValid(poly: AnyPolygonFeature): ValidationResult {
	const polygons = toPolygonArray(poly);
	const defectivePoints: PointFeature[] = [];

	for (const polyFeature of polygons) {
		// Guard: each ring needs at least 4 positions (3 unique + closing) for a valid polygon
		for (const ring of polyFeature.geometry.coordinates) {
			if (!ring || ring.length < 4) {
				return { isValid: false, message: "Polygon ring has too few coordinates", defectivePoints: [] };
			}
		}

		const outerRing = polyFeature.geometry.coordinates[0];
		const outerPoly = { ...polyFeature, geometry: { type: "Polygon" as const, coordinates: [outerRing] } };
		const outerLine = polygonToLine(outerPoly);

		// Self-intersection in outer ring
		const selfIntersect = lineIntersect(outerLine, outerLine);
		for (const pt of getRealIntersections(selfIntersect, outerRing)) {
			defectivePoints.push({ ...pt, properties: { error: "Self-intersection in outer ring" } });
		}

		// Check holes
		const holes = polyFeature.geometry.coordinates.slice(1);
		for (const hole of holes) {
			const holePoly = { ...polyFeature, geometry: { type: "Polygon" as const, coordinates: [hole] } };
			const holeLine = polygonToLine(holePoly);

			// Hole self-intersection
			const holeSelf = lineIntersect(holeLine, holeLine);
			for (const pt of getRealIntersections(holeSelf, hole)) {
				defectivePoints.push({ ...pt, properties: { error: "Self-intersection in hole" } });
			}

			// Hole vs outer ring
			const holeOuter = lineIntersect(outerLine, holeLine);
			for (const pt of getRealIntersections(holeOuter, outerRing)) {
				defectivePoints.push({ ...pt, properties: { error: "Hole intersects outer ring" } });
			}
		}

		// Holes vs holes
		for (let i = 0; i < holes.length; i++) {
			for (let j = i + 1; j < holes.length; j++) {
				const holeAPoly = { ...polyFeature, geometry: { type: "Polygon" as const, coordinates: [holes[i]] } };
				const holeBPoly = { ...polyFeature, geometry: { type: "Polygon" as const, coordinates: [holes[j]] } };
				const holeALine = polygonToLine(holeAPoly);
				const holeBLine = polygonToLine(holeBPoly);
				const inter = lineIntersect(holeALine, holeBLine);
				for (const pt of getRealIntersections(inter, holes[i])) {
					defectivePoints.push({ ...pt, properties: { error: "Holes intersect each other" } });
				}
			}
		}
	}

	// MultiPolygon: check parts against each other
	for (let i = 0; i < polygons.length; i++) {
		for (let j = i + 1; j < polygons.length; j++) {
			const result = polygonOverlaps(polygons[i], polygons[j]);
			if (result.overlaps) {
				if (result.intersectionPoints.length > 0) {
					for (const pt of result.intersectionPoints) {
						defectivePoints.push({ ...pt, properties: { error: "Polygons overlap" } });
					}
				} else {
					// Containment: mark centroid of smaller polygon
					const areaA = area(polygons[i]);
					const areaB = area(polygons[j]);
					const smaller = areaA < areaB ? polygons[i] : polygons[j];
					defectivePoints.push({
						type: "Feature",
						geometry: { type: "Point", coordinates: centroid(smaller).geometry.coordinates },
						properties: { error: "Polygons overlap" },
					});
				}
			}
		}
	}

	if (defectivePoints.length > 0) {
		return {
			isValid: false,
			message: `Found ${defectivePoints.length} geometry error(s)`,
			defectivePoints,
		};
	}

	return { isValid: true, defectivePoints: [] };
}

/**
 * Checks if two polygons overlap (border intersection or containment).
 * Shared vertices are ignored — only real crossings count.
 * Returns false if one is fully inside a hole of the other.
 */
export function polygonOverlaps(
	polyA: AnyPolygonFeature,
	polyB: AnyPolygonFeature,
): { overlaps: boolean; intersectionPoints: PointFeature[] } {
	const intersectionPoints: PointFeature[] = [];
	const polyALinesRaw = polygonToLine(polyA);
	const polyBLinesRaw = polygonToLine(polyB);

	// 1. Check border intersections (outer + holes)

	// Normalize to arrays
	const polyALines = polyALinesRaw.type === "FeatureCollection" ? polyALinesRaw.features : [polyALinesRaw];
	const polyBLines = polyBLinesRaw.type === "FeatureCollection" ? polyBLinesRaw.features : [polyBLinesRaw];

	// Two polygons touching at a vertex only → all intersections are shared vertices → ignored → returns false.
	// Two polygons crossing or overlapping → at least one intersection is not a shared vertex → returns true.
	for (const aLine of polyALines) {
		for (const bLine of polyBLines) {
			const intersections = lineIntersect(aLine, bLine);
			const real = intersections.features.filter((f) => {
				const [x, y] = f.geometry.coordinates;

				// Check if intersection is a vertex of polyA or polyB
				const isVertexA =
					polyA.geometry.type === "Polygon"
						? polyA.geometry.coordinates.some((ring) => ring.some(([vx, vy]) => vx === x && vy === y))
						: polyA.geometry.coordinates.some((poly) =>
								poly.some((ring) => ring.some(([vx, vy]) => vx === x && vy === y)),
							);

				const isVertexB =
					polyB.geometry.type === "Polygon"
						? polyB.geometry.coordinates.some((ring) => ring.some(([vx, vy]) => vx === x && vy === y))
						: polyB.geometry.coordinates.some((poly) =>
								poly.some((ring) => ring.some(([vx, vy]) => vx === x && vy === y)),
							);
				// If it's a shared vertex, ignore it
				return !(isVertexA && isVertexB);
			});

			intersectionPoints.push(...real);
		}
	}

	if (intersectionPoints.length > 0) {
		return { overlaps: true, intersectionPoints };
	}

	// 2. Check containment cases (if no border intersection)
	const polyAPolys = toPolygonArray(polyA);
	const polyBPolys = toPolygonArray(polyB);

	for (const aPoly of polyAPolys) {
		for (const bPoly of polyBPolys) {
			if (booleanContains(aPoly, bPoly) || booleanContains(bPoly, aPoly)) {
				// Check if contained in a hole
				const aHoles = aPoly.geometry.coordinates.slice(1).map((c) => polygon([c]));
				if (aHoles.some((hole) => booleanWithin(bPoly, hole))) continue;

				const bHoles = bPoly.geometry.coordinates.slice(1).map((c) => polygon([c]));
				if (bHoles.some((hole) => booleanWithin(aPoly, hole))) continue;

				return { overlaps: true, intersectionPoints: [] };
			}
		}
	}

	return { overlaps: false, intersectionPoints: [] };
}

/** Ensure each ring has matching first/last vertex (GeoJSON spec). */
function closeRings(coords: Position[][]): Position[][] {
	return coords.map((ring) => {
		if (ring.length < 2) return ring;
		const first = ring[0];
		const last = ring[ring.length - 1];
		if (first[0] !== last[0] || first[1] !== last[1]) {
			return [...ring, [...first]];
		}
		return ring;
	});
}

/**
 * Build a clean polygon feature from raw coordinates — strips bbox,
 * closes unclosed rings, and ensures a plain {type, coordinates} geometry.
 * Turf's booleanContains uses bbox for quick rejection; a stale bbox
 * (left over from pre-edit geometry via object spread) causes false negatives.
 */
function cleanPolygonFeature(feat: Feature<Polygon>): Feature<Polygon> {
	return polygon(closeRings(feat.geometry.coordinates));
}

/**
 * How far outside a polygon a vertex may sit and still count as inside.
 *
 * Zones are drawn to fill their boundary, so their vertices land *on* boundary edges — and
 * deciding "is this point inside" for a point on the edge is a coin flip in floating point.
 * The on-edge test beneath turf's booleanContains needs a cross product of exactly zero, and a
 * vertex lying part-way along a boundary segment misses that by around 1e-17, which sends it
 * down the crossing-number path to an arbitrary verdict. On one real job six of ten zones were
 * reported outside the boundary while sitting flush on the line, none of them further out than
 * a ten-thousandth of a millimetre.
 *
 * A centimetre is six orders of magnitude clear of that noise, and far below an error anyone
 * could draw on a map, let alone one that would matter in a field.
 */
const CONTAINMENT_TOLERANCE_M = 0.01;

/**
 * Containment by the same rule turf uses — every vertex of `b` inside `a` — but allowing a
 * vertex to sit `toleranceM` outside. Only consulted once the exact test has already said no,
 * so it can only ever forgive a hairline, never widen a real overlap.
 */
function containsWithinTolerance(a: Feature<Polygon>, b: Feature<Polygon>, toleranceM: number): boolean {
	const scale = metresPerDegree(b.geometry.coordinates[0][0][1]);
	return b.geometry.coordinates.every((ring) =>
		ring.every(
			(vertex) => booleanPointInPolygon(vertex, a) || distanceToEdges(vertex, a.geometry, scale) <= toleranceM,
		),
	);
}

interface PolygonContainsOptions {
	/**
	 * How far outside `firstPoly` a vertex of `secondPoly` may sit and still count as inside,
	 * in metres. Defaults to `CONTAINMENT_TOLERANCE_M`. `0` is turf's exact test alone, which is
	 * Mission Control's `firstPolygonContainsSecondPolygon` (shared-ts, tolerance 0): the
	 * mapmaking ports of MC's zone rules pass it to keep MC's verdicts.
	 */
	toleranceM?: number;
}

/**
 * Returns true if firstPoly fully contains secondPoly (accounting for holes).
 * All parts of secondPoly must be inside firstPoly's outer ring and not inside any hole.
 *
 * Defensively rebuilds features before checking — geometry objects may carry stale
 * bbox or unclosed rings that break turf's booleanContains.
 */
export function polygonContains(
	firstPoly: AnyPolygonFeature,
	secondPoly: AnyPolygonFeature,
	{ toleranceM = CONTAINMENT_TOLERANCE_M }: PolygonContainsOptions = {},
): boolean {
	const firstPolys = toPolygonArray(firstPoly);
	const secondPolys = toPolygonArray(secondPoly);

	// All parts of secondPoly must be contained somewhere
	return secondPolys.every((secondPolyPart) =>
		firstPolys.some((firstPolyPart) => {
			const a = cleanPolygonFeature(firstPolyPart);
			const b = cleanPolygonFeature(secondPolyPart);

			if (!booleanContains(a, b) && !(toleranceM > 0 && containsWithinTolerance(a, b, toleranceM))) return false;

			const holes = a.geometry.coordinates.slice(1).map((c) => polygon([c]));
			return !holes.some((hole) => booleanWithin(b, hole));
		}),
	);
}
